import React from 'react';
import styled from 'styled-components';
import axios from 'axios';

// Base compartilhada das telas de documento (Documentos Complementares e futuras declarações):
// formato de pessoa/veículo dos formulários, formatações, trechos de texto e os estilos da folha A4.
// Trazido do Truck Control e adaptado ao cadastro de clientes do Nexus (cpf/cnpj separados, identidade = RG).

const enderecoVazio = () => ({
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: ''
});

export const pessoaVazia = () => ({
  idCliente: null,
  nome: '',
  cpfCnpj: '',
  rg: '',
  telefone: '',
  email: '',
  ...enderecoVazio()
});

export const veiculoVazio = () => ({
  marcaModelo: '',
  cor: '',
  placa: '',
  chassi: '',
  renavam: '',
  motor: '',
  ano: ''
});

export function removerMascaraCPFCNPJ(documento) {
  if (typeof documento === 'string') return documento.replace(/\D/g, '');
  if (typeof documento === 'number' || typeof documento === 'bigint') return documento.toString();
  return '';
}

export function formatarCpfCnpj(valor) {
  const num = removerMascaraCPFCNPJ(valor || '');
  if (!num) return '';
  if (num.length <= 11) {
    return num.padStart(11, '0').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return num.padStart(14, '0').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

// Formata enquanto digita, sem completar com zeros (000.000.000-00 ou 00.000.000/0000-00)
export function mascaraCpfCnpj(valor) {
  const v = removerMascaraCPFCNPJ(valor || '').slice(0, 14);
  const montar = (partes, separadores) => partes.reduce((acc, [ini, fim], i) => {
    const pedaco = v.slice(ini, fim);
    return pedaco ? acc + (i > 0 ? separadores[i - 1] : '') + pedaco : acc;
  }, '');
  if (v.length <= 11) return montar([[0, 3], [3, 6], [6, 9], [9, 11]], ['.', '.', '-']);
  return montar([[0, 2], [2, 5], [5, 8], [8, 12], [12, 14]], ['.', '.', '/', '-']);
}

export function formatarCep(valor) {
  const num = (valor || '').replace(/\D/g, '').slice(0, 8);
  return num.length <= 5 ? num : `${num.slice(0, 5)}-${num.slice(5)}`;
}

// Busca de endereço via ViaCEP — nunca bloqueia o formulário se falhar ou não encontrar.
export async function buscarCep(cepValor) {
  const cep = (cepValor || '').replace(/\D/g, '');
  if (cep.length !== 8) return null;
  try {
    const resp = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
    if (resp.data && !resp.data.erro) {
      return {
        logradouro: resp.data.logradouro || '',
        bairro: resp.data.bairro || '',
        cidade: resp.data.localidade || '',
        uf: resp.data.uf || ''
      };
    }
    return null;
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
}

export const CAMPO_EM_BRANCO = '______________';

export function rotuloDocumento(valor) {
  return removerMascaraCPFCNPJ(valor || '').length > 11 ? 'CNPJ' : 'CPF';
}

// "Canguçu-RS"
export function municipioUf(p) {
  return [p.cidade, p.uf].filter(Boolean).join('-');
}

export const escapeHtml = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function formatarMoeda(valor) {
  return (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Cliente do cadastro do Nexus → formato de pessoa dos formulários. No Nexus o CPF e o CNPJ ficam em
// campos separados e o RG é a "identidade".
export function clienteParaPessoa(cliente) {
  return {
    idCliente: cliente.idCliente,
    nome: cliente.nome || '',
    cpfCnpj: removerMascaraCPFCNPJ(cliente.cpf || cliente.cnpj || ''),
    rg: cliente.identidade || '',
    telefone: cliente.telefone || '',
    email: cliente.email || '',
    cep: cliente.cep || '',
    logradouro: cliente.logradouro || '',
    numero: cliente.numero || '',
    complemento: cliente.complemento || '',
    bairro: cliente.bairro || '',
    cidade: cliente.cidade || '',
    uf: cliente.uf || ''
  };
}

// ─── Bloco de endereço reutilizável ───────────────────────────────────────────

export const EnderecoCampos = ({ valor, onChange, onCepChange, buscando }) => (
  <>
    <FormRow>
      <InputGroup>
        <Label>CEP</Label>
        <Input
          value={valor.cep}
          onChange={(e) => onCepChange(e.target.value)}
          placeholder="00000-000"
        />
        {buscando && <HintText>Buscando endereço...</HintText>}
      </InputGroup>
      <InputGroup style={{ flex: 2 }}>
        <Label>Cidade</Label>
        <Input value={valor.cidade} onChange={(e) => onChange('cidade', e.target.value)} placeholder="Canguçu" />
      </InputGroup>
      <InputGroup style={{ flex: '0 0 70px' }}>
        <Label>UF</Label>
        <Input
          value={valor.uf}
          onChange={(e) => onChange('uf', e.target.value.toUpperCase())}
          placeholder="RS"
          maxLength={2}
          style={{ textTransform: 'uppercase' }}
        />
      </InputGroup>
    </FormRow>
    <FormRow>
      <InputGroup style={{ flex: 2 }}>
        <Label>Logradouro</Label>
        <Input value={valor.logradouro} onChange={(e) => onChange('logradouro', e.target.value)} placeholder="Rua Joaquim Paulo de Freitas" />
      </InputGroup>
      <InputGroup>
        <Label>Número</Label>
        <Input value={valor.numero} onChange={(e) => onChange('numero', e.target.value)} placeholder="140" />
      </InputGroup>
    </FormRow>
    <FormRow>
      <InputGroup>
        <Label>Complemento</Label>
        <Input value={valor.complemento} onChange={(e) => onChange('complemento', e.target.value)} placeholder="Casa, apto, bloco..." />
      </InputGroup>
      <InputGroup>
        <Label>Bairro</Label>
        <Input value={valor.bairro} onChange={(e) => onChange('bairro', e.target.value)} placeholder="Triângulo" />
      </InputGroup>
    </FormRow>
  </>
);

// ─── Estilos (mesmos da Procuração do Truck Control) ─────────────────────────

export const PageBackground = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #e6f7f5 0%, #d4efed 100%);
`;

export const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #004d4d;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  font-family: 'Segoe UI', Arial, sans-serif;
`;

export const ConfigButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: white;
  color: #004d4d;
  border: 1.5px solid #0f766e;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 128, 128, 0.15);
  padding: 20px;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
`;

export const CardTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: #004d4d;
  margin: 0;
  font-weight: 700;
`;

export const SubTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  color: #008080;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 10px 0 10px 0;
`;

export const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;
  &:hover { transform: translateY(-1px); }
`;

export const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const InputGroup = styled.div`
  flex: 1;
  min-width: 140px;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #34495e;
`;

export const HintText = styled.span`
  display: block;
  color: #7f8c8d;
  font-size: 11.5px;
  margin-top: 4px;
  font-style: italic;
`;

export const ErrorText = styled.span`
  display: block;
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
`;

export const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${(props) => (props.hasError ? '#e74c3c' : '#ddd')};
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#e74c3c' : '#008080')};
    box-shadow: 0 0 0 3px ${(props) => (props.hasError ? 'rgba(231,76,60,0.1)' : 'rgba(0,128,128,0.1)')};
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 13px;
`;

export const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

export const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: linear-gradient(135deg, #004d4d 0%, #008080 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,128,128,0.35); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: #f1f3f5;
  color: #34495e;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #e9ecef; }
`;

// ── Preview / documento impresso ────────────────────────────────────────────

export const PreviewWrapper = styled.div`
  max-width: 842px;
  margin: 0 auto 40px auto;
  padding: 0 24px;
`;

export const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 12px 18px;
  border-radius: 10px 10px 0 0;
  border: 1px solid rgba(0,128,128,0.15);
  border-bottom: none;
  font-weight: 700;
  color: #004d4d;
  font-size: 14px;
`;

export const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
`;

export const SelectField = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
`;

export const ModelosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
`;

export const ModeloCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  padding: 14px;
  background: ${(props) => (props.$ativo ? '#f0fdfa' : 'white')};
  border: 2px solid ${(props) => (props.$ativo ? '#008080' : '#e5e7eb')};
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  strong { font-size: 14px; color: #004d4d; }
  span { font-size: 12px; color: #7f8c8d; }
  &:hover { border-color: #008080; }
`;

export const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 8px 14px;
  border: 1px solid rgba(0,128,128,0.15);
  border-bottom: none;
`;

export const FerramentaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  background: white;
  color: #34495e;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #e6f4f4; color: #004d4d; }
`;

export const Separador = styled.span`
  width: 1px;
  height: 22px;
  background: #ced4da;
  margin: 0 4px;
`;

// Folha A4 no formato do modelo: fonte sem serifa (estilo Calibri), texto justificado, margens largas.
// O conteúdo é HTML puro (editável), estilizado pelos seletores abaixo.
export const PaperSheet = styled.div`
  background: white;
  padding: 16mm 26mm;
  border: 1px solid rgba(0,128,128,0.15);
  border-radius: 0 0 10px 10px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.1);
  font-family: Calibri, Carlito, 'Segoe UI', Arial, sans-serif;
  font-size: 11pt;
  color: #111;
  line-height: 1.5;
  outline: none;
  min-height: 200px;
  cursor: text;

  &:focus { box-shadow: 0 6px 24px rgba(0,0,0,0.1), 0 0 0 2px rgba(0,128,128,0.35); }

  /* Cabeçalho do escritório: identificação pequena, em negrito, alinhada à esquerda */
  .cab { text-align: left; margin-bottom: 22px; }
  .cab p {
    font-family: Arial, Helvetica, sans-serif;
    font-weight: 700;
    font-size: 7pt;
    line-height: 1.3;
    text-align: left;
    margin: 0;
    text-transform: uppercase;
  }

  h1 { text-align: center; font-size: 16pt; font-weight: 700; margin: 0 0 24px 0; text-transform: uppercase; }
  p.par { text-align: justify; font-size: 11pt; margin: 0 0 14px 0; }

  /* Dados do veículo um embaixo do outro (placa, chassi, renavam, marca, cor), rótulos em negrito */
  .veiculo {
    margin: 14px 0 18px 0;
    font-size: 11.5pt;
    line-height: 1.45;
  }

  /* Modelo mais longo (causa própria): tipografia um pouco mais compacta para caber em uma página A4 */
  &.compacto p.par { font-size: 10.5pt; line-height: 1.4; margin-bottom: 9px; }
  &.compacto .veiculo { margin: 8px 0 12px 0; font-size: 11pt; line-height: 1.35; }
  &.compacto .fecha { margin-top: 14px; }
  &.compacto .fecha .data { margin-bottom: 28px; }

  /* Data em branco e linha de assinatura à direita, como no modelo */
  .fecha { width: 62%; margin-left: auto; text-align: center; margin-top: 26px; page-break-inside: avoid; }
  .fecha .data { font-size: 11pt; margin: 0 0 40px 0; }
  .fecha .linha { border-top: 1px solid #333; margin-bottom: 6px; }
  .fecha span { font-size: 11pt; }
`;

// ── Modais ────────────────────────────────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalBox = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  h3 { margin: 0; font-size: 16px; color: #2c3e50; }
`;

export const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #7f8c8d;
`;

export const ModalContent = styled.div`
  padding: 18px 20px;
`;

export const SearchRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
`;

export const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ResultItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border: 1px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  strong { font-size: 14px; color: #2c3e50; }
  span { font-size: 12.5px; color: #7f8c8d; }
  &:hover { background: #f1f8fe; border-color: #008080; }
`;

